import {
  PERMISSION_LABELS,
  PERMISSIONS,
  type Permission,
  RoleInput,
  withinPermissions,
} from '@elsa/contracts';
import { Badge, Button, Card, ErrorBox, Field, Input, Loading, Note, Page } from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useOrg } from '../lib/session.tsx';
import { rpc } from '../lib/supabase.ts';
import type { Team, TeamRole } from '../lib/types.ts';

// Rolurile firmei (docs/23): fiecare firma porneste cu sabloanele (Director, Administrator,
// Manager vanzari, Agent vanzari, Membru, Vizualizare) si le poate schimba sau adauga altele.
// Proprietarul nu se editeaza. O permisiune pe care nu o ai nu o poti da (bifa e blocata);
// aceeasi regula o aplica SQL-ul (app.save_role).

const GRANTABLE = PERMISSIONS.filter((p) => p !== 'org.owner');

function PermissionBoxes({
  value,
  onChange,
  mine,
  disabled,
  idPrefix,
}: {
  value: Permission[];
  onChange: (v: Permission[]) => void;
  mine: Permission[];
  disabled: boolean;
  idPrefix: string;
}) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-2">
      {GRANTABLE.map((p) => {
        const locked = disabled || !mine.includes(p);
        return (
          <label
            key={p}
            htmlFor={`${idPrefix}-${p}`}
            className={`flex items-start gap-2 text-sm ${locked ? 'text-gri-600' : ''}`}
            title={!disabled && locked ? 'Nu ai această permisiune, deci nu o poți da' : undefined}
          >
            <input
              id={`${idPrefix}-${p}`}
              type="checkbox"
              className="mt-0.5"
              checked={value.includes(p)}
              disabled={locked}
              onChange={(e) =>
                onChange(e.target.checked ? [...value, p] : value.filter((x) => x !== p))
              }
            />
            {PERMISSION_LABELS[p]}
          </label>
        );
      })}
    </div>
  );
}

function RoleCard({ role, mine }: { role: TeamRole; mine: Permission[] }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? '');
  const [perms, setPerms] = useState<Permission[]>(role.permissions);
  const editable = !role.is_owner && withinPermissions(mine, role);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['team'] });
    void qc.invalidateQueries({ queryKey: ['orgs'] });
  };
  const save = useMutation({
    mutationFn: () => {
      const input = RoleInput.parse({ name, description, permissions: perms });
      return rpc('save_role', {
        p_org: org.id,
        p_id: role.id,
        p_name: input.name,
        p_description: input.description ?? null,
        p_permissions: input.permissions,
      });
    },
    onSuccess: refresh,
  });
  const del = useMutation({
    mutationFn: () => rpc('delete_role', { p_org: org.id, p_id: role.id }),
    onSuccess: refresh,
  });
  const changed =
    name !== role.name ||
    description !== (role.description ?? '') ||
    [...perms].sort().join() !== [...role.permissions].sort().join();
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {role.name}
          {role.is_owner ? <Badge tone="info">toate drepturile</Badge> : null}
          <Badge>
            {role.members} {role.members === 1 ? 'membru' : 'membri'}
          </Badge>
        </span>
      }
    >
      {role.is_owner ? (
        <p className="text-sm text-gri-700">
          Proprietarul are toate permisiunile și e singurul care poate preda firma. Rolul nu se
          schimbă; proprietarul se schimbă din Echipă → „Predă firma”.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Numele rolului">
              <Input
                value={name}
                maxLength={60}
                disabled={!editable}
                onChange={(e) => setName(e.target.value)}
                aria-label={`Numele rolului ${role.name}`}
              />
            </Field>
            <Field label="Descriere">
              <Input
                value={description}
                maxLength={300}
                disabled={!editable}
                onChange={(e) => setDescription(e.target.value)}
                aria-label={`Descrierea rolului ${role.name}`}
              />
            </Field>
          </div>
          <PermissionBoxes
            value={perms}
            onChange={setPerms}
            mine={mine}
            disabled={!editable}
            idPrefix={role.id}
          />
          {editable ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                tone="primary"
                disabled={!changed || save.isPending}
                onClick={() => save.mutate()}
              >
                Salvează rolul
              </Button>
              <Button
                tone="danger"
                disabled={role.members > 0 || del.isPending}
                title={role.members > 0 ? 'Mută întâi membrii pe alt rol' : undefined}
                onClick={() => {
                  if (confirm(`Ștergi rolul „${role.name}”?`)) del.mutate();
                }}
              >
                Șterge
              </Button>
              {save.isSuccess && !changed ? (
                <span className="text-xs text-gri-700">Salvat.</span>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-gri-700">
              Rolul are permisiuni pe care tu nu le ai, deci nu-l poți schimba.
            </p>
          )}
          <ErrorBox error={save.error ?? del.error} />
        </div>
      )}
    </Card>
  );
}

function NewRole({ mine }: { mine: Permission[] }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [perms, setPerms] = useState<Permission[]>(['data.read']);
  const create = useMutation({
    mutationFn: () => {
      const input = RoleInput.parse({ name, description, permissions: perms });
      return rpc('save_role', {
        p_org: org.id,
        p_id: null,
        p_name: input.name,
        p_description: input.description ?? null,
        p_permissions: input.permissions,
      });
    },
    onSuccess: () => {
      setName('');
      setDescription('');
      setPerms(['data.read']);
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
  return (
    <Card title="Rol nou">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Numele rolului">
            <Input
              name="role_name"
              value={name}
              maxLength={60}
              placeholder="ex. Agent zona Moldova"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Descriere (opțional)">
            <Input
              name="role_description"
              value={description}
              maxLength={300}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </div>
        <PermissionBoxes
          value={perms}
          onChange={setPerms}
          mine={mine}
          disabled={false}
          idPrefix="nou"
        />
        <Button
          tone="primary"
          disabled={name.trim().length < 2 || create.isPending}
          onClick={() => create.mutate()}
        >
          Creează rolul
        </Button>
        <ErrorBox error={create.error} />
      </div>
    </Card>
  );
}

export function RolesPage() {
  const { org } = useOrg();
  const q = useQuery({
    queryKey: ['team', org.id],
    queryFn: () => rpc<Team>('team', { p_org: org.id }),
  });
  const mine = q.data?.my_permissions ?? org.permissions;
  return (
    <Page title="Roluri">
      <p className="text-sm text-gri-800">
        Ce poate face fiecare om din firmă. Un rol fără „vede toate județele” vede doar județele
        alocate fiecărui membru din Echipă (de exemplu agenții de vânzări).
      </p>
      <ErrorBox error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : (
        <>
          {q.data?.roles.map((r) => (
            <RoleCard key={`${r.id}:${r.permissions.join()}:${r.name}`} role={r} mine={mine} />
          ))}
          <NewRole mine={mine} />
          <Note>
            Meniul din Elsa urmează rolul: fiecare vede doar paginile pentru care are drept.
          </Note>
        </>
      )}
    </Page>
  );
}
